"use client"

import { CheckCircle2, AlertCircle, Info } from "lucide-react"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/RadixToast"
import { useToast } from "@/hooks/useToast"

// Leading-icon mapping by variant — keeps the toast scannable at a glance
// and lets us match icon color to the same accent the left border uses.
function VariantIcon({ variant }: { variant?: string | null }) {
  if (variant === "success") {
    return (
      <CheckCircle2
        className="w-5 h-5 text-accent flex-shrink-0 mt-0.5"
        strokeWidth={1.75}
      />
    )
  }
  if (variant === "destructive") {
    return (
      <AlertCircle
        className="w-5 h-5 text-status-busy flex-shrink-0 mt-0.5"
        strokeWidth={1.75}
      />
    )
  }
  return (
    <Info
      className="w-5 h-5 text-text-secondary flex-shrink-0 mt-0.5"
      strokeWidth={1.75}
    />
  )
}

// Auto-dismiss windows by intent. Indefinite (Infinity) for action-bearing
// toasts so users always have time to engage the action; longer window for
// errors so the message can be read; default 5s for routine confirmation.
const DEFAULT_DURATION = 5_000;
const ERROR_DURATION = 8_000;

export function Toaster() {
  const { toasts } = useToast()

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, variant, duration, ...props }) {
        const resolvedDuration =
          typeof duration === 'number'
            ? duration
            : action
            ? Infinity
            : variant === 'destructive'
            ? ERROR_DURATION
            : DEFAULT_DURATION
        return (
          <Toast key={id} variant={variant} duration={resolvedDuration} {...props}>
            <VariantIcon variant={variant ?? undefined} />
            <div className="flex-1 min-w-0">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && (
                <ToastDescription>{description}</ToastDescription>
              )}
            </div>
            {action}
            <ToastClose />
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
